/**
 * Shared auth controls — the submit bar, the divider, and the text link.
 *
 * These exist so the seven auth screens stop each inventing their own: before
 * this rebuild the submit button was `size="lg"` on four screens and default on
 * two, the "or" divider was copy-pasted three times with three different
 * capitalisations, and the back-links carried three different focus-ring
 * strings. That drift is invisible on any single screen and obvious across a
 * flow.
 *
 * The submit bar is 48px (above the 44px floor — this is the most important
 * control on the page and it is pressed one-handed), full width, and states
 * what it is doing while it does it. The spinner uses `.g-spin`, which the
 * global stylesheet pins to its resting frame under `prefers-reduced-motion`;
 * the label change is what carries the state for those users.
 */
import { Loader2 } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";

import { ICON, ICON_STROKE } from "@/lib/icons";

export interface AuthSubmitProps {
  /** Idle label. */
  children: ReactNode;
  /** Label while the request is in flight. */
  pendingLabel: string;
  pending: boolean;
  disabled?: boolean;
  testId: string;
}

export function AuthSubmit({
  children,
  pendingLabel,
  pending,
  disabled = false,
  testId,
}: AuthSubmitProps): ReactElement {
  /*
   * The design's submit: #16453F, 14px radius, 19px padding, 17px/600, hover
   * #0E2A26. It is a plain <button> rather than the shadcn `Button` because
   * that component carries the product's `--g-*` variants, and this screen is
   * a different visual world. Type, disabled semantics, the pending label and
   * the testid are unchanged.
   */
  return (
    <button
      type="submit"
      disabled={disabled || pending}
      data-testid={testId}
      className="gav2-submit"
      style={{
        background: "#16453F",
        color: "#F0E9DA",
        border: "none",
        borderRadius: 14,
        padding: "var(--gav2-submit-py) 19px",
        fontSize: 17,
        fontWeight: 600,
        cursor: disabled || pending ? "default" : "pointer",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        opacity: disabled || pending ? 0.72 : 1,
      }}
    >
      {pending ? (
        <>
          <Loader2
            size={ICON.md}
            strokeWidth={ICON_STROKE}
            className="g-spin"
            aria-hidden="true"
          />
          <span>{pendingLabel}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * The "or continue with" rule. A drawn line with the label sitting IN it —
 * the armature made literal, rather than two grey bars flanking grey text.
 */
export function AuthDivider({ label }: { label: string }): ReactElement {
  /* The design's divider: two hairlines with mono caps between them. */
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }} aria-hidden="true">
      <span style={{ flex: 1, height: 1, background: "rgba(23,32,29,0.15)" }} />
      <span
        style={{
          // IBM Plex Mono in every language, Kyrgyz included (design-world.css).
          fontFamily: "var(--gd-mono)",
          fontSize: 11.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "#6E6A5E",
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, height: 1, background: "rgba(23,32,29,0.15)" }} />
    </div>
  );
}

const AUTH_LINK_BASE =
  "rounded-[var(--g-radius-sm)] font-medium underline-offset-4 " +
  "hover:underline focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-background";

/**
 * The default text link — `--g-primary-ink`, NOT `--g-primary`.
 *
 * Measured, not preferred: brand teal `#1F5D5E` on the dark ground `#121110`
 * is 2.5:1, which axe-core reports as a serious WCAG 1.4.3 failure and which a
 * user in bright daylight simply cannot read. `--g-primary-ink` is the token
 * that exists for exactly this — it darkens to `#103A3B` on cream (11.5:1) and
 * lightens to a mixed teal on the dark ground (6.5:1), so ONE class is correct
 * in both themes and in all four palettes. Verified with an axe run on all
 * seven auth routes in light and dark.
 */
export const AUTH_LINK_CLASS = AUTH_LINK_BASE + " text-[#16453F] hover:text-[#C75A34]";

/** A quieter link — "back to sign-in" under a primary action. */
export const AUTH_LINK_MUTED_CLASS = AUTH_LINK_BASE + " text-[#4A473D] hover:text-[#C75A34]";

/**
 * A link sitting INSIDE a sentence ("No account? Create one").
 *
 * WCAG 2.5.8 exempts targets inline in a block of text, and stretching one to
 * 44px would break the line it lives in. Vertical padding on an `inline`
 * element expands the hit box WITHOUT affecting line layout, so these get a
 * ~45px tall target for a thumb while the sentence renders exactly as before.
 */
export const AUTH_INLINE_LINK_CLASS = AUTH_LINK_CLASS + " py-3.5";

export interface AuthLinkProps {
  to: string;
  children: ReactNode;
  testId?: string;
  /** `primary` (default) or `muted` for a secondary escape hatch. */
  tone?: "primary" | "muted";
  /** Extra utilities — size/spacing only; colour comes from `tone`. */
  className?: string;
}

/** A text link with a real 44px-tall hit area on touch. */
export function AuthLink({
  to,
  children,
  testId,
  tone = "primary",
  className,
}: AuthLinkProps): ReactElement {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={
        "inline-flex min-h-[2.75rem] items-center " +
        (tone === "muted" ? AUTH_LINK_MUTED_CLASS : AUTH_LINK_CLASS) +
        (className !== undefined ? " " + className : "")
      }
    >
      {children}
    </Link>
  );
}
